/**
 * Data palettes: colours that are DATA, not brand roles. Customer-configurable
 * calendar defaults, category and series palettes validated for colour vision,
 * and the defaults a user-chosen colour falls back to. They live here so no
 * dashboard file keeps a colour literal, but they are deliberately NOT mapped
 * to the brand tokens: the design-system README treats calendar visit colours
 * as customer-configurable categories, and a series palette is tuned for
 * separation, not identity. Change a value here only on purpose; the consumers
 * read it verbatim.
 */

const freeze = (value) => {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.values(value).forEach(freeze);
    Object.freeze(value);
  }
  return value;
};

/** Scheduler calendar defaults (config/scheduler/appearanceDefaults.ts). */
const calendarAppearance = {
  categories: {
    personal_care: { primary: "#15803D", background: "#86EFAC" },
    meals: { primary: "#BE123C", background: "#FDA4AF" },
    daily_support: { primary: "#475569", background: "#E2E8F0" },
    supervision: { primary: "#6D28D9", background: "#C4B5FD" },
    social_activities: { primary: "#B45309", background: "#FDE68A" },
    cleaning: { primary: "#2563EB", background: "#93C5FD" },
    laundry: { primary: "#0F766E", background: "#5EEAD4" },
    shopping: { primary: "#C2410C", background: "#FDBA74" },
    household: { primary: "#65A30D", background: "#BEF264" },
    alarm: { primary: "#B91C1C", background: "#FCA5A5" },
    medical: { primary: "#9D174D", background: "#FBCFE8" },
    mobility: { primary: "#0369A1", background: "#BAE6FD" },
    social: { primary: "#9A3412", background: "#FED7AA" },
    other: { primary: "#44403C", background: "#D6D3D1" },
    unknown: { primary: "#6B7280", background: "#9CA3AF" },
  },
  frequencyBorders: {
    daily: "#3b82f6",
    weekly: "#22c55e",
    bi_weekly: "#f59e0b",
    "3_weekly": "#ec4899",
    monthly: "#8b5cf6",
    quarterly: "#14b8a6",
    yearly: "#6366f1",
    custom: "#6b7280",
  },
  specialEvents: {
    break: { color: "#78716c", textColor: "#fafaf9" },
    waiting: { color: "#94a3b8", textColor: "#64748b" },
    idle: { color: "#818cf8", textColor: "#4338ca" },
    travel: { color: "#b0bec5", textColor: "#546e7a" },
  },
  modifierBadges: {
    pinned: { background: "#fef3c7", color: "#d97706" },
    movable: { background: "#f3e8ff", color: "#7c3aed" },
    multiStaff: { background: "#dbeafe", color: "#2563eb" },
    highPriority: { background: "#fee2e2", color: "#dc2626" },
    dependency: { background: "#d1fae5", color: "#065f46" },
    skills: { background: "#fef9c3", color: "#854d0e" },
    tags: { background: "#f0fdf4", color: "#166534" },
    template: { background: "#eff6ff", color: "#1d4ed8" },
    insetGroup: { background: "#fdf4ff", color: "#7e22ce" },
    overlapAllowed: { background: "#cffafe", color: "#0e7490" },
    allocation: { background: "#f0fdf4", color: "#15803d" },
  },
  /** Mirrors the seed districts in dashboard-server and @appcaire/shared. */
  districts: {
    Kärrtorp: "#92400E",
    Björkhagen: "#166534",
    Bagarmossen: "#86EFAC",
    Skarpnäck: "#FDE68A",
    Enskededalen: "#FB923C",
    Central: "#A78BFA",
    "Gamla Enskede": "#1E3A8A",
    Blåsut: "#5EEAD4",
    Sandsborg: "#FBCFE8",
    "Tallkrogen/Gubbängen": "#94A3B8",
  },
  districtFallback: "#94A3B8",
  /** Client-view preferred time window band (ClientViewAppearance defaults). */
  clientViewTimeWindow: {
    background: "rgba(59,130,246,0.10)",
    border: "rgba(59,130,246,0.30)",
  },
};

/** Care-plan weekday chart segments per inset label (carePlanInsetChartColors.ts). */
const carePlanInsets = {
  unknown: { primary: "#6B7280", background: "#CBD5E1" },
  toilet: { primary: "#0369A1", background: "#7DD3FC" },
  dressing: { primary: "#6D28D9", background: "#DDD6FE" },
  bathing: { primary: "#0E7490", background: "#A5F3FC" },
  selfCare: { primary: "#C2410C", background: "#FED7AA" },
  meals: { primary: "#BE123C", background: "#FDA4AF" },
  supervision: { primary: "#5B21B6", background: "#C4B5FD" },
  cleaning: { primary: "#1D4ED8", background: "#BFDBFE" },
  laundry: { primary: "#0F766E", background: "#5EEAD4" },
  shopping: { primary: "#713F12", background: "#DCC9B4" },
  outdoors: { primary: "#A21CAF", background: "#F5D0FE" },
  transfer: { primary: "#A16207", background: "#FEF08A" },
  escort: { primary: "#854D0E", background: "#FEF9C3" },
  social: { primary: "#B45309", background: "#FDE68A" },
  alarm: { primary: "#991B1B", background: "#FECACA" },
};

/** Preset swatches a planner picks a tag colour from (tagManagementModel.ts). */
const tags = [
  "#3b82f6",
  "#ef4444",
  "#22c55e",
  "#f59e0b",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#f97316",
  "#6366f1",
  "#14b8a6",
];

/** Employee colour defaults (Employee.color picker, client-row visit fill). */
const employees = {
  /** The native colour picker's value while the stored colour is empty. */
  swatchFallback: "#3b82f6",
  /** Client-row visit fill when no caregiver is assigned. */
  unassignedVisit: "#6b7280",
};

/** Visit flex levels, ordinal blue ramp plus three usage hues (flexLevelAppearance.ts). */
const flexLevels = {
  levels: {
    exact: "#104281",
    narrow: "#1c5cab",
    slot: "#2a78d6",
    shift: "#5598e7",
    "flex-before": "#1baf7a",
    "flex-after": "#4a3aa7",
    "flex-both": "#eda100",
    wide: "#86b6ef",
  },
  supply: "#64748b",
  gap: "#d03b3b",
  separator: "#ffffff",
};

/** Shift-type categories on the Bryntum grid, bars and badges (shiftTypeVisual.ts). */
const shiftTypes = {
  gridTint: {
    day: "#dbeafe",
    evening: "#e0e7ff",
    night: "#ede9fe",
    split: "#ccfbf1",
    other: "#e2e8f0",
  },
  event: {
    day: "#c2410c",
    evening: "#d97706",
    night: "#5b21b6",
    split: "#0f766e",
    other: "#64748b",
  },
  unifiedView: "#0d9488",
  badge: {
    day: { background: "#fff7ed", color: "#9a3412", border: "#fed7aa" },
    evening: { background: "#fffbeb", color: "#b45309", border: "#fde68a" },
    night: { background: "#f5f3ff", color: "#5b21b6", border: "#ddd6fe" },
    split: { background: "#f0fdfa", color: "#0f766e", border: "#99f6e4" },
    other: { background: "#f8fafc", color: "#64748b", border: "#e2e8f0" },
  },
};

/** Analys series outside the domain roles, CVD-validated (analysColors.ts). */
const analys = {
  idle: "#8f8a80",
  recoverable: "#e9a58c",
};

/**
 * A hue generated at runtime for a data series (per-chain stripes, per-group
 * accents), as `hsl(h s% l%)` or `hsl(h s% l% / alpha)`. Series only, never
 * chrome: chrome takes a token.
 */
export function seriesHsl(hue, saturation, lightness, alpha) {
  const channels = `${hue} ${saturation}% ${lightness}%`;
  return alpha == null ? `hsl(${channels})` : `hsl(${channels} / ${alpha})`;
}

export const dataPalettes = freeze({
  calendarAppearance,
  carePlanInsets,
  tags,
  employees,
  flexLevels,
  shiftTypes,
  analys,
});
